import { useEffect } from 'react';
import { fmtMoney, currencySymbol } from '../../money';
import { DataBar } from '../DataBar';
import type { BarDot, BarMarker } from '../DataBar';
import Spark from './Spark';
import { throughputReadingLong } from './movement-read';
import { ReportTrend } from './trend';
import { healthStrip } from '../../portfolio-trend';
import { MilestoneTable, SlipTrendLine } from './milestone';
import { projectMilestones, slipSeries, nextMilestone } from '../../milestones';
import { withMilestoneForecasts } from '../../milestone-forecast';
import { ForecastView } from '../InsightPanels';
import { richHasText, richText } from '../../rich-text';
import { RISK_CAP } from '../../charter';
import { ProgressMini } from '../ProgressMini';
import Info from '../Info';
import Icon from '../Icon';
import type { ProgressHealth } from '../../progress-health';
import { scheduleHealthSeverity } from '../../progress-health';
import type { ProgressCard } from '../../portfolio';
import { EVM_COST_FORECAST_MIN_PCT, costBasisRead, COST_BASIS_LABELS } from '../../portfolio';
import type { ProjectStatusProps, Rag, MovementCounts } from './types';

/** Enter/Space on a link-styled control, so a breadcrumb is operable by keyboard
 *  as well as mouse. */
function onActivate(fn: () => void) {
  return (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fn(); }
  };
}

/**
 * ONE PROJECT'S STATUS REPORT — the drill that opens from a portfolio roll-up
 * row. A mockup-faithful build of the approved view
 * (dev-preview/portfolio/view-report.html): the head, then a grid of cards —
 * Health (worst of three), The numbers, Waiting on, Phases, and Moved since the
 * last pack. Everything is read from the derived `row`; every control is wired
 * to its real callback (no PDF button — that is a later increment).
 */

/** Months for a tiny YYYY-MM-DD → "20 Sep" day formatter. */
import { MONTHS } from '../../when';

/** A plain `YYYY-MM-DD` day as "20 Sep", or '' when absent/unparseable. */
function fmtDay(day?: string): string {
  if (!day) return '';
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return '';
  return `${d} ${MONTHS[m - 1]}`;
}

/** A plain `YYYY-MM-DD` day at LOCAL midnight — the same frame `dueDayDiff` uses,
 *  so the plan-vs-pace gap is counted in whole local days. */
function planMsOf(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(y, m - 1, d).getTime();
}

/** The cost-performance word a non-finance reader skims: above 1.05 the budget is
/** The schedule-performance word: ahead of the planned value, on it,
 *  or behind it. */
function schedWord(spi: number | null, rag: Rag | null): string {
  if (spi != null && spi > 1.05) return 'Ahead';
  return rag === 'green' ? 'On schedule' : 'Behind';
}

/** The RAG badge class the app's `.rag` shape reads. */
const RAG_CLASS: Record<Rag, 'g' | 'a' | 'r'> = { green: 'g', amber: 'a', red: 'r' };
/** The DataBar tone a reconciled status maps to, so a gauge dot colours by the same
 *  worst-of read the drill speaks — never green over a board the drill calls behind. */
const RAG_TONE: Record<Rag, 'good' | 'warn' | 'bad'> = { green: 'good', amber: 'warn', red: 'bad' };
/** A compact money figure for an inline bar label — "TT$335k" — in the active currency,
 *  so a wide amount sits above a marker without clipping. The full figure lives in the
 *  hover and the key/value rows. */
const fmtMoneyK = (n: number): string => `${currencySymbol()}${Math.round(n / 1000).toLocaleString()}k`;
/** Severity order, so two reads can be reconciled to the worse of the two. */
const RAG_RANK: Record<Rag, number> = { green: 0, amber: 1, red: 2 };
const worseRag = (a: Rag, b: Rag): Rag => (RAG_RANK[a] >= RAG_RANK[b] ? a : b);
/** The word shown in a RAG badge or a signal grade — plain-language severity, not
 *  the colour name (the badge already carries the colour). */
const RAG_WORD: Record<Rag, string> = { green: 'On track', amber: 'At risk', red: 'Off track' };
/** The colour a signal grade word is drawn in, matching the mockup. */
const RAG_INK: Record<Rag, string> = {
  red: 'var(--p-critical)',
  amber: 'var(--s-inprogress-ink)',
  green: 'var(--s-done-ink)',
};

/** A short, sentence-cap label for what a waiting item is waiting on. */
const TYPE_LABEL: Record<string, string> = {
  item: 'Item',
  person: 'Person',
  decision: 'Decision',
  external: 'External',
  test: 'Test',
  design: 'Design',
  bundled: 'Bundled',
  parked: 'Parked',
  other: 'Other',
  '': 'Waiting',
};

/** How many waiting items the report lists before deferring the rest to the
 *  blocked-cards drill — a long list otherwise unbalances the two-column grid. */
const WAIT_CAP = 6;
/** The same cap for the Progress drill — the slipping cards listed before the rest
 *  roll into a "+N more" line, so the section stays the size of its grid neighbour. */
const PROG_CAP = 6;

/** The counts headline under the Progress section head — "N at risk · N behind",
 *  dropping a zero so it never reads "0 at risk". The section only renders when
 *  something is slipping, so the empty fallback is a safety net, not a shown state. */
const progHead = (c: { atRisk: number; behind: number }): string =>
  [c.atRisk ? `${c.atRisk} at risk` : '', c.behind ? `${c.behind} behind` : '']
    .filter(Boolean)
    .join(' · ') || 'all on track';

/** Rebuild the minimal `ProgressHealth` a {@link ProgressMini} reads from a drill
 *  card — the roll-up carries the %, expected %, band and word, so the bar colours
 *  by the same health the counts came from and draws the expected-% tick the gap is
 *  measured against. */
const miniHealth = (c: ProgressCard): ProgressHealth => ({
  kind: c.kind,
  pct: c.pct,
  expectedPct: c.expectedPct,
  label: c.label,
  tone: c.kind === 'at-risk' ? 'bad' : 'warn',
  reason: '',
});

/** A due day as whole days from today (local midnight): negative = already late,
 *  0 = due today, positive = still to come. Drives the plain lateness phrase. */
const dueDayDiff = (day: string, nowMs: number): number => {
  const [y, m, d] = day.split('-').map(Number);
  if (!y || !m || !d) return 0;
  const due = new Date(y, m - 1, d).getTime();
  const t = new Date(nowMs);
  const today0 = new Date(t.getFullYear(), t.getMonth(), t.getDate()).getTime();
  return Math.round((due - today0) / 86_400_000);
};

/** "due 24 Oct · 1 day late" / "due 25 Oct · due today" / "due 27 Oct · in 3 days" —
 *  the target date with a plain phrase saying how late or how soon, so the reader
 *  sees WHY a card is flagged, not only that it is. */
const dueLatePhrase = (day: string | undefined, nowMs: number): string => {
  if (!day) return '';
  const diff = dueDayDiff(day, nowMs);
  const base = `due ${fmtDay(day)}`;
  if (diff < 0) { const n = -diff; return `${base} · ${n} day${n === 1 ? '' : 's'} late`; }
  if (diff === 0) return `${base} · due today`;
  return `${base} · in ${diff} day${diff === 1 ? '' : 's'}`;
};
/** The severity band WORD (Critical/High/Medium/Low from the register's one L×I scale)
 *  mapped to its band colour class — the steering surfaces show severity, not the impact
 *  word (Q2). The word carries the rank; the colour is only a flag (R12). */
const severityBandKey = (severity: string): string =>
  /\bcritical\b/i.test(severity)
    ? 'critical'
    : /\bhigh\b/i.test(severity)
      ? 'high'
      : /\bmedium\b/i.test(severity)
        ? 'medium'
        : /\blow\b/i.test(severity)
          ? 'low'
          : '';

export default function ProjectStatus({ row, pulses, phases, milestones, milestonePulses, cards, calendar, onBack, onOpenBoard, onOpenBlockers, onOpenCard }: ProjectStatusProps) {
  // Open a card from any reference on the report — the shared handler for the
  // waiting, slipping and work-in-flight lists, so "NET-123" clicks through to the
  // card. Keyboard-operable (Enter/Space) like the breadcrumb.
  const openCard = (ref?: string) => { if (ref && onOpenCard) onOpenCard(row.id, ref); };

  // The project's milestone set (0124), and the slip series for the headline
  // milestone's trend line — the go-live when there is one, else the nearest open
  // milestone, so a project run to phase gates still shows a trend.
  const now = Date.now();
  // Each milestone enriched with its cadence forecast (full trials for the drill) from
  // the board's own cards — the table reads the predicted finish, not the today-floor.
  // `cards` is always supplied by the Portfolio; default to none so the report still
  // renders (cold-start milestones, no forecast) when a caller has none to hand.
  const msSet = withMilestoneForecasts(projectMilestones(row, phases, milestones, now, cards ?? []), cards ?? [], now, undefined, calendar);
  const trendMs = msSet.find((m) => m.key === 'golive') ?? msSet.find((m) => m.kind === 'golive') ?? nextMilestone(msSet, now);
  // The go-live's cadence forecast, for the headline card + the numbers card's honest
  // "plan → predicted" and "chance the plan date is met" rows. Ready only when the
  // board has enough finished history; otherwise the card falls back to its cold state.
  // The whole-board go-live (`key === 'golive'`, milestones.ts) carries the row's own
  // cadence projection. A project may ALSO have a manager-created Go-Live-*kind*
  // standalone with no forecast tie that sorts earlier by date (L172) — never let that
  // win the headline, or the band vanishes and THE NUMBERS reads "on plan" while the
  // milestone table shows the real slip. Prefer the synthesized entry, then any ready
  // golive, then a cold golive for the fallback state.
  const goliveMs =
    msSet.find((m) => m.key === 'golive') ??
    msSet.find((m) => m.kind === 'golive' && m.forecast?.ready) ??
    msSet.find((m) => m.kind === 'golive');
  const gf = goliveMs?.forecast;
  const trendSeries = trendMs
    ? slipSeries(milestonePulses.filter((p) => p.milestoneKey === trendMs.key), trendMs.plannedDay)
    : [];

  const hasDates = !!(row.planDay && row.forecastDay);
  const over = row.variance ? row.variance.diff > 0 : false;
  const canDrillBlockers = row.waiting.some((w) => w.ref);

  // The go-live band's reconciliation (surface 1): the board's own pace against the
  // committed plan date, in whole local days. Measured on the 85%-confident date (p85) —
  // the honest headline this report leads with everywhere (Vinnie, 29 Oct), the same
  // date the big forecast band and the milestone table show — so the verdict can never
  // disagree with the date beside it. Only meaningful with a ready forecast.
  const glReady = !!(hasDates && gf && gf.ready && row.planDay);
  const glGapDays = glReady ? Math.round((gf!.p85 - planMsOf(row.planDay!)) / 86_400_000) : 0;
  const glLate = glGapDays > 0;
  // The manager's own hand-entered forecast, shown BESIDE the derived EAC (not driving
  // the verdict — Vinnie, 29 Oct): its gap to the baseline, in plain words.
  const mgrPct = row.forecast != null && row.budgetBase ? ((row.forecast - row.budgetBase) / row.budgetBase) * 100 : null;

  // THE SCHEDULE READ, RECONCILED WITH THE BOARD'S OWN PACE (D1, 29 Oct). The row's
  // `schedule` signal is phase-date integrity — are the committed dates still ahead?
  // The go-live band is a second lens — at this pace, when does the work actually land?
  // They are legitimately different, but the report must never show "On track — on plan"
  // in the Health card beside a band that predicts the date missed by weeks. When a READY
  // cadence forecast predicts the go-live materially past the plan, the report's schedule
  // verdict takes the worse of the two and says so in the pace's words, so the headline
  // pill, the Health card's Schedule row and the band all tell one story. Thresholds
  // mirror scheduleSignal's own (amber past the plan, red past 21 days). The roll-up LIST
  // stays the lighter phase-date glance — it shows no pace band to contradict.
  const paceRag: Rag = glReady && glLate ? (glGapDays > 21 ? 'red' : 'amber') : 'green';
  // THE THIRD SCHEDULE LENS — card progress against the clock (finding 3, Vinnie
  // 29 Oct). The phase-date read asks "are the committed dates still ahead?" and the
  // pace band asks "at this throughput, when does it land?" — but the pace band needs
  // finished-work history and goes quiet in cold-start, and phase dates can read
  // nominally fine while the work underneath them falls badly behind the calendar.
  // So a board a third behind on the actual work could still show "On track". This
  // lens closes that gap: it needs no history, folds in UNCONDITIONALLY, and can go
  // red on its own (a quarter of the scope's worth of work missing vs the clock), so
  // the headline can never sit "On track" over a materially-behind board.
  const progRag: Rag = scheduleHealthSeverity(row.progressSchedule);
  const schedRag = worseRag(worseRag(row.schedule.rag, paceRag), progRag);
  // Whichever lens is the worst also speaks the verdict, so the words match the
  // colour. On a tie the pace forecast (a dated prediction) leads the phase-date
  // read; the progress lens leads only when it is strictly the worst — it is the
  // cold-start alarm the other two miss, so when it alone fires it must be heard.
  const paceLeads =
    glReady && glLate &&
    RAG_RANK[paceRag] >= RAG_RANK[row.schedule.rag] &&
    RAG_RANK[paceRag] >= RAG_RANK[progRag];
  const progLeads =
    RAG_RANK[progRag] > RAG_RANK[row.schedule.rag] && RAG_RANK[progRag] > RAG_RANK[paceRag];
  const schedWhy = progLeads
    ? row.progressSchedule.label.toLowerCase()
    : paceLeads
      ? `pace predicts ${glGapDays} day${glGapDays === 1 ? '' : 's'} late`
      : row.schedule.why;
  // The headline can only get worse once the schedule lenses are folded in, never
  // better. When a lens (pace OR progress-vs-clock) is what drove the schedule to
  // red — and the base health wasn't red already — the headline must say so in that
  // lens's words, never fall back to a "healthy" line that contradicts the colour.
  const reportHealth: Rag = row.onHold ? 'green' : worseRag(row.health ?? 'green', schedRag);
  const reportHealthWhy = row.onHold
    ? 'on hold'
    : row.health !== 'red' && schedRag === 'red' && (paceLeads || progLeads)
      ? (progLeads ? 'behind on the work done' : 'behind the pace to go live')
      : row.healthWhy;

  const ragClass = row.onHold ? 'hold' : RAG_CLASS[reportHealth];
  const ragWord = row.onHold ? 'On hold' : RAG_WORD[reportHealth];

  // Escape backs out of the drill — a keyboard way out, matching the lens tabs,
  // the Filter/Display menus and the lean card panel.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // A card opened FROM the report sits on top in `.overlay.pf-scene`; let it take
      // Escape and close only itself. Backing the report out here too would over-close,
      // dumping the user to the Portfolio list on one keystroke (L173 / regresses L25, L38).
      if (document.querySelector('.overlay.pf-scene')) return;
      onBack();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onBack]);

  return (
    <div>
      <div className="pf-crumbs">
        <a role="button" tabIndex={0} onClick={onBack} onKeyDown={onActivate(onBack)}>Portfolio</a> › <span className="here">{row.name}</span>
      </div>

      <div className="pf-report-head">
        <div>
          <div className="pf-report-title">
            <span className={`rag ${ragClass}`}><span className="dot"></span>{ragWord}</span>
            {row.name}
          </div>
          <div className="pf-report-meta">
            Owner <b>{row.owner}</b>
            {row.sponsor && <> · Sponsor <b>{row.sponsor}</b></>}
            {row.startDay && <> · Started <b>{fmtDay(row.startDay)}</b></>}
            {hasDates && (
              row.slipDays > 0 ? (
                <> · Go-Live: was <b>{fmtDay(row.planDay)}</b>, now <b>{fmtDay(row.forecastDay)}</b></>
              ) : (
                <> · Go-Live <b>{fmtDay(row.forecastDay)}</b></>
              )
            )}
          </div>
          {/* The one calm "how far along" read — the granular project % and, when
              it carries a signal, the plain-words schedule line. The headline RAG
              pill above stays the go-live verdict, deliberately distinct. */}
          <div className="pf-report-prog">
            Project <b>{row.projectPct}% done</b>
            {row.progressSchedule.kind !== 'none' && (() => {
              // The progress-vs-clock phrase reads "the work done so far", one lens of the schedule.
              // When it is green but the worst-of schedule (pace forecast / phase dates) is behind,
              // the bare word "On schedule" here verbally contradicts the "Behind" verdict on the
              // same screen (pm-witness, 29/31 Oct). So when they disagree, QUANTIFY the work-vs-plan
              // position instead of saying "On schedule" — a number can't contradict the pill.
              const progGoodButBehind = row.progressSchedule.tone === 'good' && schedRag !== 'green';
              const tone = progGoodButBehind
                ? 'neutral'
                : row.progressSchedule.tone === 'good' ? 'good'
                : row.progressSchedule.tone === 'bad' ? 'bad'
                : row.progressSchedule.tone === 'warn' ? 'warn' : 'neutral';
              const behindBy = row.progressSchedule.behindBy;
              return (
                <>
                  {' · '}
                  <span className={`pf-sched is-${tone}`}>
                    {progGoodButBehind
                      ? (behindBy != null && Math.abs(behindBy) >= 1
                          ? <>{Math.abs(behindBy)}% {behindBy < 0 ? 'ahead of' : 'behind'} the plan on the work done so far</>
                          : <>level with the plan on the work done so far</>)
                      : row.progressSchedule.label}
                  </span>
                </>
              );
            })()}
          </div>
        </div>
        <div className="pf-report-actions">
          <button className="btn" onClick={() => onOpenBoard(row.id)}>Open the board →</button>
        </div>
      </div>

      {/* THE STATUS HEADLINE + THE READS BEHIND IT (Surface 6A, Metrics Layer C). The
          plain-language verdict and the pace forecast are COMBINED into one headline block
          (Vinnie, 31 Oct — "combine them"), so the go-live date and its confidence are
          stated ONCE, not duplicated across a separate verdict band and forecast panel.
          Below it, the two reads a PMO/CFO steers by: the date-truth Earned Schedule —
          SPI(t), the schedule index in TIME, drawn as a gauge so it never competes with
          the one finish date — and cost (CPI) with its trend-aware EAC range. The worked
          formula is NEVER shown: the index, the plain-language read and the tooltip carry
          the trust (Vinnie, 31 Oct — "keep the formula hidden"). Everything reads off what
          the drill already derives (row.earnedSchedule + row.evm + the pace forecast); no
          new capture. Shown when there is a schedule, cost OR go-live read to show. */}
      {(row.evm || (row.earnedSchedule && row.earnedSchedule.spiT != null) || gf) && (() => {
        const es = row.earnedSchedule;
        const esReady = !!(es && es.spiT != null);
        const evm = row.evm;
        // (F3, monitoring-witness 1 Nov) The index reads ONLY the dated subset and discloses how
        // many cards that is. One or two dated cards give a confident-looking "0.00 · Behind" that
        // is really one card's slip — it must not read as a board-wide verdict. So disclose the
        // basis prominently, and when it is THIN soften the standalone number to a provisional read
        // (the reconciled RAG beside it still speaks the drill's real verdict). The read is never
        // withheld or floored — the count disclosure is the honest answer to a thin plan (Vinnie:
        // "disclose the count, don't hide the read").
        const esDatedTotal = cards ? cards.filter((c) => c.status !== 'wontdo').length : null;
        const esThin = esReady && es!.scheduled <= 2;
        // ONE schedule status for the whole drill (Vinnie, 31 Oct — "you cannot be behind
        // schedule and on schedule at the same time"). The Earned Schedule index is a single,
        // optimistic lens; it must never read GREENER than the drill's authoritative schedule
        // verdict (schedRag = worst of phase-dates, pace forecast, progress-vs-clock — the one
        // the header and Health card speak). So the section's status is the WORSE of the two:
        // the 0.97 index is still shown honestly, but it can't declare "On schedule" over a
        // board the rest of the page calls behind. This is the same "the comfortable number
        // never governs" rule as the money-SPI footnote.
        const sectSchedR: Rag = esReady ? worseRag(es!.rag ?? 'green', schedRag) : schedRag;
        const costR: Rag = evm?.cpiRag ?? 'green';
        const bandR = worseRag(sectSchedR, costR);
        // How the declared cost basis shapes the cost read — on fixed price CPI tracks
        // payment timing not effort, so the exposure + forecast verdict lead; on time &
        // materials CPI is a genuine efficiency read (0219). Absent = the balanced read.
        const cbRead = costBasisRead(row.costBasis);
        const cbLabel = row.costBasis ? COST_BASIS_LABELS[row.costBasis] : null;
        const cap = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);
        // How far the SPI(t) index sits off "on plan", in plain percent — for the one-line
        // read in the schedule card (0.84 → "about 16% behind in time").
        const timeGapPct = esReady ? Math.round(Math.abs(1 - es!.spiT!) * 100) : 0;

        // The plain-language verdict speaks the DRILL'S schedule verdict in its own words (so
        // the band, the Health card and the header never disagree), plus a budget clause from
        // the forecast variance. Green only when the reconciled schedule status is green.
        const schedLead =
          sectSchedR === 'green'
            ? <><span className="ok">On</span> schedule</>
            : schedRag !== 'green'
              // The drill itself is behind — speak its verdict in its own words, so the band
              // and the Health card read identically.
              ? <><span className="late">{cap(schedWhy)}</span></>
              // Only the earned-schedule index is behind (the rest reads on track) — name that,
              // never borrow a phrase that reads "on track" beside a behind status.
              : <><span className="late">Behind the earned-schedule plan</span></>;
        const budgetLead = row.variance
          ? (row.variance.diff > 0
              ? <><span className="late">{row.variance.pct.toFixed(0)}% over</span> budget</>
              : row.variance.diff < 0 && Math.abs(row.variance.pct) >= 5
                ? <><span className="ok">{Math.abs(row.variance.pct).toFixed(0)}% under</span> budget</>
                : <><span className="ok">on budget</span></>)
          : null;

        return (
          <>
            <div className="es-headline">
              <div className="verdict-band">
                <div className="vb-top">
                  <span className={`vb-ico ${RAG_CLASS[bandR]}`} aria-hidden="true"><Icon name="chart" size={19} /></span>
                  <div className="vb-words">
                    <div className="vb-lead">
                      {schedLead && budgetLead ? <>{schedLead} · {budgetLead}</> : schedLead || budgetLead || <>Tracking the project</>}
                    </div>
                    <div className="vb-sub">
                      {/* Budget context only — the go-live DATE lives once, in the forecast below,
                          so it is never printed twice. With no pace forecast, the Earned Schedule
                          finish stands in as the schedule read. */}
                      {evm && evm.eac != null && (evm.costReady || evm.eacBasis === 'manager' || evm.eacBasis === 'fixed')
                        ? <>{evm.eacBasis === 'manager' ? <>The manager’s forecast puts this at <b>{fmtMoney(evm.eac)}</b></> : evm.eacBasis === 'fixed' ? <>The fixed-price contract holds this at <b>{fmtMoney(evm.eac)}</b></> : <>Spending projects <b>{fmtMoney(evm.eac)}</b></>}{row.budgetBase != null ? <> against a <b>{fmtMoney(row.budgetBase)}</b> baseline.</> : <>.</>}</>
                        : row.budgetBase != null
                          ? <>Measured against the committed go-live and a <b>{fmtMoney(row.budgetBase)}</b> baseline.</>
                          : <>Measured against the committed go-live.</>}
                      {!gf && esReady && es!.forecastFinishISO && <> Earned Schedule puts the finish near <b>{fmtDay(es!.forecastFinishISO)}</b>.</>}
                    </div>
                  </div>
                </div>
              </div>
              {gf && <ForecastView f={gf} title="Go-Live forecast" sub="When this project is likely to go live, from the board’s own delivery pace" />}
            </div>

            {(row.evm || esReady) && (
            <div className="es-wrap">
            <div className="es-sched">
              {/* SCHEDULE — the date-truth read. */}
              <div className="pf-card2">
                <h3>Schedule — Earned Schedule</h3>
                <div className="evm-tile-k">
                  <Info
                    title="Earned Schedule"
                    what="Earned Schedule tells you the schedule truth in time, not money. It asks where in the plan today's finished work was meant to land, then compares that to how much time has actually gone by. Unlike the value-based schedule index, it stays honest right up to the finish — so when it says you're behind, you really are."
                  >Schedule performance · SPI(t)</Info>
                </div>
                {esReady ? (
                  <>
                    <div className="evm-tile-row">
                      <span className={`rag ${RAG_CLASS[sectSchedR]}`}><span className="dot" />{schedWord(es!.spiT, sectSchedR)}</span>
                      <span className={`evm-idx${esThin ? ' is-provisional' : ''}`}>{es!.spiT!.toFixed(2)}</span>
                    </div>
                    {/* (F3) The basis, prominent: the index reads the dated subset, so say how
                        many cards that is. A thin subset (1–2 dated) reads provisional — one
                        card's slip should not look like a board-wide verdict. */}
                    <div className={`es-basis${esThin ? ' is-thin' : ''}`}>
                      {esThin
                        ? <>Provisional — reads just <b>{es!.scheduled}</b> dated card{es!.scheduled === 1 ? '' : 's'}{esDatedTotal != null ? <> of {esDatedTotal}</> : null}. Add due dates on the Plan to firm it up.</>
                        : <>Reads <b>{es!.scheduled}</b> dated card{es!.scheduled === 1 ? '' : 's'}{esDatedTotal != null ? <> of {esDatedTotal}</> : null}.</>}
                    </div>
                    {/* The index as a GAUGE, not a rival finish date — one date lives on this
                        screen (the pace forecast in the headline), so two can't compete. The
                        dot's POSITION is the honest index value; its COLOUR follows the
                        reconciled status, so the gauge never reads green over a behind board. */}
                    <DataBar
                      density="drill"
                      ariaLabel={`Earned Schedule ${es!.spiT!.toFixed(2)} — ${schedWord(es!.spiT, sectSchedR)}`}
                      zones={[{ from: 2, to: 50, tone: 'behind' }, { from: 50, to: 98, tone: 'ahead' }]}
                      markers={[{
                        pct: 50,
                        soft: true,
                        tip: { title: 'On Plan · 1.00', what: 'The line where finished work matches the plan. Left of it the plan is running behind in time; right of it, ahead.' },
                      }]}
                      dots={[{
                        pct: (es!.spiT! - 0.5) * 100,
                        tone: RAG_TONE[sectSchedR],
                        value: es!.spiT!.toFixed(2),
                        tip: {
                          title: `Time-based · SPI(t) ${es!.spiT!.toFixed(2)}`,
                          what: <>The honest read — about <b>{timeGapPct}% {es!.spiT! < 1 ? 'behind' : 'ahead'}</b> in time, and it stays true right to the finish. The date to steer by is the {gf ? 'pace forecast above' : 'committed go-live'}.</>,
                        },
                      }]}
                      ends={{ left: 'Behind', mid: 'On Plan · 1.00', right: 'Ahead' }}
                    />
                    {evm && evm.spi != null && (
                      <div className="dbar-sub">
                        <span className="dbar-sub-dot" />
                        <div className="dbar-sub-body"><b>Money-based index (SPI) · {evm.spi.toFixed(2)}</b> — the classic value index. It drifts toward 1.00 as the work wraps, so it reads healthier than the project is: a supporting number, not the one to steer by.</div>
                      </div>
                    )}
                    <div className="dbar-foot">The date to steer by is the <b>{gf ? 'pace forecast above' : 'committed go-live'}</b> — this index says the plan is slipping, not when it lands.</div>
                  </>
                ) : (
                  <div className="evm-foot">Earned Schedule needs cards with planned due dates and some elapsed time to read. Add dates on the Plan to see the time-based schedule index here.</div>
                )}
              </div>

              {/* COST — cost performance and the estimate at completion. */}
              <div className="pf-card2">
                <h3>Cost — CPI</h3>
                <div className="evm-tile-k">
                  <Info
                    title="Cost performance (CPI)"
                    what={cbRead.cpiMeaningful
                      ? "How the value of the work done so far compares with the money actually paid out. On time and materials the spend tracks the effort, so this is a genuine read of cost efficiency. Even so, whether the project lands within budget is best told by the estimate at completion set against the baseline."
                      : "How the value of the work done so far compares with the money actually paid out. Where a vendor's price is fixed by contract, this leans on when payments fall due more than on efficiency, and it eases toward its mid-point as the work wraps — so read it as a supporting signal. Whether the project will land within budget is better told by the estimate at completion set against the baseline."}
                  >Cost performance · CPI</Info>
                  {cbLabel && <span className="evm-basis">{cbLabel}</span>}
                </div>
                {evm && (evm.costReady || evm.eacBasis === 'fixed') ? (
                  <>
                    <div className="evm-tile-row">
                      <span className={`rag ${RAG_CLASS[row.budget?.rag ?? evm.cpiRag ?? 'green']}`}><span className="dot" />{row.budget?.rag === 'red' ? 'Over Budget' : row.budget?.rag === 'amber' ? 'Trending Over' : 'On Budget'}</span>
                      {evm.cpi != null && <span className={`evm-idx${evm.eacBasis === 'fixed' ? ' is-supporting' : ''}`}>{evm.cpi.toFixed(2)}</span>}
                    </div>
                    <div className="evm-foot">{evm.eacBasis === 'fixed'
                      ? 'On a fixed-price contract the final cost is the agreed contract value, not a projection from spend — so the index below is a supporting read of payment timing, never an overrun. The verdict is the contract value against the baseline.'
                      : cbRead.cpiMeaningful
                      ? 'On time and materials the spend tracks effort, so the index reads true as cost efficiency so far. The verdict is still the estimate at completion against the baseline, below.'
                      : 'The index is cost efficiency so far — a supporting number that eases toward 1.00 as the work wraps, and on fixed-price spend tracks when payments fall due. The verdict is the estimate at completion against the baseline, below.'}</div>
                    {row.budgetBase != null && row.budgetBase > 0 && row.committed > 0 && (() => {
                      // Fixed-price proxy: once most of the baseline is on contracts, the steering
                      // cost signal is EXPOSURE (committed + paid vs baseline), not CPI — so lead
                      // with it here rather than leaving it a footnote (pm-witness, 1 Nov). A project
                      // DECLARED fixed price leads with it always, not only past the commitment proxy.
                      const committedPct = Math.round((row.committed / row.budgetBase) * 100);
                      if (!cbRead.leadWithExposure && committedPct < 50) return null;
                      const exposurePct = Math.round(((row.committed + row.actual) / row.budgetBase) * 100);
                      const favourable = !row.variance || row.variance.diff <= 0;
                      const tight = favourable && exposurePct >= 90;
                      return <div className={`evm-foot${tight ? ' warn' : ''}`}><b>{committedPct}% of the baseline is committed</b> — {fmtMoney(row.committed + row.actual)} of {fmtMoney(row.budgetBase)} committed or spent{tight ? ', little headroom left' : ''}.</div>;
                    })()}
                    <div className="num-sec2">
                      {row.budgetBase != null && <div className="kv2"><span className="k"><Info title="Baseline budget (BAC)" what="The budget you set out to deliver this project for — the approved baseline every cost read is measured against.">Baseline (BAC)</Info></span><span className="v">{fmtMoney(row.budgetBase)}</span></div>}
                      {row.committed > 0 && <div className="kv2"><span className="k"><Info title="Committed + actual" what="Money you've already promised on purchase orders and contracts, plus what's actually been paid. It shows your real exposure — not just what has left the bank so far.">Committed + actual</Info></span><span className="v">{fmtMoney(row.committed + row.actual)}</span></div>}
                      <div className="kv2"><span className="k"><Info title="Spent so far (AC)" what="The money that has actually gone out the door to date — real invoices paid, not amounts committed or forecast. It's what cost performance is measured against.">Spent so far (AC)</Info></span><span className="v">{fmtMoney(row.actual)}</span></div>
                    </div>
                    {row.committed > 0 && <div className="evm-foot">Cost performance reads paid spend only — committed money is shown separately, not counted as spend.</div>}
                    {evm.eacBasis === 'trend' && evm.eacCpi != null && evm.eac != null && Math.round(evm.eacCpi) !== Math.round(evm.eac) && (() => {
                      // A money axis (not a percentage) carrying the estimate RANGE: the
                      // steadier cost-to-date anchor → the recent-pace estimate, with the
                      // budget as a reference marker. Padded so no marker sits at the very
                      // edge (which would clip its inline figure). Reuses the one DataBar kit.
                      const anchor = evm.eacCpi!, pace = evm.eac!, bac = row.budgetBase ?? null;
                      const vals = bac != null ? [anchor, pace, bac] : [anchor, pace];
                      const lo = Math.min(...vals), hi = Math.max(...vals);
                      const span = hi - lo || 1;
                      const pad = span * 0.18;
                      const pos = (x: number) => ((x - (lo - pad)) / (hi - lo + 2 * pad)) * 100;
                      const markers: BarMarker[] = bac != null
                        ? [{ pct: pos(bac), soft: true, label: fmtMoneyK(bac), tip: { title: `Budget · ${fmtMoney(bac)}`, what: 'The approved baseline the estimate is measured against.' } }]
                        : [];
                      const dots: BarDot[] = [
                        { pct: pos(anchor), tone: 'neutral', tip: { title: `At today's cost rate · ${fmtMoney(anchor)}`, what: 'Where the total lands if the rest costs what the work so far has — the steadier anchor.' } },
                        { pct: pos(pace), tone: over ? 'bad' : 'good', value: fmtMoneyK(pace), tip: { title: `Recent-pace estimate · ${fmtMoney(pace)}`, what: <>The projected total at the recent spend rate{row.variance ? <> — about <b>{Math.abs(row.variance.pct).toFixed(0)}% {row.variance.diff > 0 ? 'over' : 'under'}</b> budget</> : null}.</> } },
                      ];
                      return (
                        <DataBar
                          density="drill"
                          ariaLabel={`Estimate at completion ${fmtMoney(pace)}, steadier anchor ${fmtMoney(anchor)}${bac != null ? `, against budget ${fmtMoney(bac)}` : ''}`}
                          fill={{ from: Math.min(pos(anchor), pos(pace)), to: Math.max(pos(anchor), pos(pace)), tone: 'warn' }}
                          markers={markers}
                          dots={dots}
                          ends={{ left: 'Baseline', right: 'Est. at Completion' }}
                        />
                      );
                    })()}
                    <div className="num-sec2">
                      <div className="kv2"><span className="k"><Info title="Estimate at completion (EAC)" what="Your best current estimate of what the whole project will cost by the time it's done, based on how spending is actually tracking — not the original budget.">Est. at completion (EAC{evm.eacBasis === 'trend' ? ' · recent pace' : evm.eacBasis === 'fixed' ? ' · contract price' : ''})</Info></span><span className={`v ${over ? 'bad' : 'ok'}`}>{fmtMoney(evm.eac)}</span></div>
                      {evm.eacBasis === 'trend' && evm.eacCpi != null && Math.round(evm.eacCpi) !== Math.round(evm.eac ?? 0) && (
                        <div className="kv2"><span className="k"><Info title="Range anchor" what="The steadier companion estimate: what the total would be if the project simply kept performing at its lifetime average. It sits beside the recent-pace figure so a single number never tells the whole story.">Range anchor (at cost to date)</Info></span><span className="v muted">{fmtMoney(evm.eacCpi)}</span></div>
                      )}
                      {row.variance ? (
                        <div className="kv2"><span className="k"><Info title="Variance at completion" what="How far the forecast total lands above or below the baseline budget — the bottom-line 'will we come in over or under' figure.">Variance at completion</Info></span>
                          {row.variance.diff > 0 ? <span className="v bad">+{fmtMoney(row.variance.diff)} · {row.variance.pct.toFixed(0)}% over</span>
                            : row.variance.diff < 0 ? <span className="v ok">−{fmtMoney(Math.abs(row.variance.diff))} · {Math.abs(row.variance.pct).toFixed(0)}% under</span>
                            : <span className="v ok">on budget</span>}
                        </div>
                      ) : null}
                      {row.forecast != null && (
                        <div className="kv2"><span className="k">Manager’s estimate</span><span className="v muted">{fmtMoney(row.forecast)}{mgrPct != null && Math.abs(mgrPct) >= 1 ? ` · ${Math.abs(mgrPct).toFixed(0)}% ${mgrPct > 0 ? 'over' : 'under'}` : ''}</span></div>
                      )}
                    </div>
                    <div className="evm-foot">{evm.eacBasis === 'fixed'
                      ? 'The estimate is the agreed contract value — a fixed-price contract cannot land over the baseline without an approved scope change, so paying ahead of the work never reads as an overrun.'
                      : evm.eacBasis === 'trend'
                      ? 'The estimate follows the recent spend rate, shown against the classic cost-to-date anchor as a range.'
                      : 'Projected from cost performance to date — the recent spend is too thin, or too front-loaded, to read a trend from.'}</div>
                  </>
                ) : evm && evm.eacBasis === 'manager' && evm.eac != null ? (
                  <>
                    <div className="evm-foot">Not enough invoices recorded yet to read cost here — showing the manager’s forecast instead.</div>
                    <div className="num-sec2">
                      {row.budgetBase != null && <div className="kv2"><span className="k"><Info title="Baseline budget (BAC)" what="The budget you set out to deliver this project for — the approved baseline every cost read is measured against.">Baseline (BAC)</Info></span><span className="v">{fmtMoney(row.budgetBase)}</span></div>}
                      <div className="kv2"><span className="k"><Info title="Spent so far (AC)" what="The money that has actually gone out the door to date — real invoices paid, not amounts committed or forecast.">Spent so far (AC)</Info></span><span className="v">{fmtMoney(row.actual)}</span></div>
                      <div className="kv2"><span className="k">Manager’s forecast</span><span className={`v ${over ? 'bad' : 'ok'}`}>{fmtMoney(evm.eac)}{mgrPct != null && Math.abs(mgrPct) >= 1 ? ` · ${Math.abs(mgrPct).toFixed(0)}% ${mgrPct > 0 ? 'over' : 'under'}` : ''}</span></div>
                      {row.variance ? (
                        <div className="kv2"><span className="k"><Info title="Variance at completion" what="How far the forecast total lands above or below the baseline budget — the bottom-line 'will we come in over or under' figure.">Variance at completion</Info></span>
                          {row.variance.diff > 0 ? <span className="v bad">+{fmtMoney(row.variance.diff)} · {row.variance.pct.toFixed(0)}% over</span>
                            : row.variance.diff < 0 ? <span className="v ok">−{fmtMoney(Math.abs(row.variance.diff))} · {Math.abs(row.variance.pct).toFixed(0)}% under</span>
                            : <span className="v ok">on budget</span>}
                        </div>
                      ) : null}
                    </div>
                  </>
                ) : evm ? (
                  <div className="evm-foot">{evm.ac > 0
                    ? <><b>Too early to read</b> — cost forecast held at {evm.pct}% complete (it reads from {EVM_COST_FORECAST_MIN_PCT}%).</>
                    : <><b>No spend yet</b> — nothing has been paid out, so there is no cost to project against.</>}</div>
                ) : (
                  <div className="evm-foot">No baseline and spend captured yet, so there is no cost read to show.</div>
                )}
              </div>
            </div>
            <div className="es-caveat">Predicted finish and estimate at completion assume today’s scope and pace; cross-card dependencies are not modelled.</div>
            </div>
            )}
          </>
        );
      })()}

      <div className="pf-grid">

        {/* The hero column (right, wider): the dates + money hub, then the milestone
            table under it. Everything else stacks in the side column (left). The two
            are independent flex stacks so cards size to their content and pack with no
            gaps between them — a tall hero no longer forces empty space beside a short
            neighbour (the old row-paired grid did). Placed by `grid-column`, so the DOM
            order here (main first) is free to keep Numbers contiguous with Milestones. */}
        <div className="pf-col pf-col-main">
        {/* "The numbers" card was removed (Vinnie, 31 Oct — one owner per fact): Progress
            lives in the header, and the committed Plan target vs the pace-predicted go-live
            (with its confidence) now lives ONCE in the merged status headline above and in
            the Milestones table below — never a third time here. */}

        {/* Milestones — planned → forecast → actual, with a slip column and the
            headline milestone's forecast trend. Full-width, directly under the
            Health / numbers row (0124). Absent when the project has no milestones
            (no go-live, no promoted phase, no standalone). */}
        {msSet.length > 0 && (
          <div className="pf-card">
            <h3>Milestones — planned → predicted → actual</h3>
            <MilestoneTable milestones={msSet} />
            {trendMs && <SlipTrendLine label={trendMs.name} series={trendSeries} />}
          </div>
        )}
        </div>

        <div className="pf-col pf-col-side">
        <div className="pf-card">
          <h3>Health — worst of three</h3>
          <div className="pf-health-top">
            <span className={`rag ${ragClass}`}><span className="dot"></span>{ragWord}</span>
            <span className="big">{reportHealthWhy}</span>
          </div>
          {/* The worst-of-three breakdown — the RAG word per dimension only. The WHY
              behind schedule and budget is stated once, in the headline and the cost card,
              so it is not repeated here (Vinnie, 31 Oct). Blockers keeps its count, which
              is the one detail shown nowhere else. */}
          <div className="pf-sig-row">
            <span className="sig-name">Schedule</span>
            <span className="sig-why"><b style={{ color: RAG_INK[schedRag] }}>{RAG_WORD[schedRag]}</b></span>
          </div>
          <div className="pf-sig-row">
            <span className="sig-name">Budget</span>
            <span className="sig-why">
              {row.budget ? (
                <b style={{ color: RAG_INK[row.budget.rag] }}>{RAG_WORD[row.budget.rag]}</b>
              ) : (
                <span style={{ color: 'var(--t3)' }}>Not enough to read cost yet</span>
              )}
            </span>
          </div>
          <div className="pf-sig-row">
            <span className="sig-name">Blockers</span>
            <span className="sig-why">
              <b style={{ color: RAG_INK[row.blockers.rag] }}>{RAG_WORD[row.blockers.rag]}</b> — {row.blockers.why}
            </span>
          </div>
          {/* The history — the daily pulses (0122) as a weekly strip, with the
              last-change arrow and a plain sentence. Absent until a pulse exists. */}
          {!row.onHold && <ReportTrend view={healthStrip(pulses, { periods: 8, periodDays: 7 })} periods={8} />}
        </div>

        <div className="pf-card">
          <h3>Waiting on — {row.waiting.length} open</h3>
          {row.waiting.length === 0 ? (
            <div className="kv"><span className="k">Nothing outstanding</span></div>
          ) : (
            <>
              {/* Show only the first handful — a long list unbalances the report and
                  the full set is one click away on "See the blocked cards". */}
              {row.waiting.slice(0, WAIT_CAP).map((w, i) => (
                <div
                  className={`wc${w.ref ? ' is-drillable' : ''}`}
                  key={w.ref ?? `${w.label}-${i}`}
                  {...(w.ref ? { role: 'button', tabIndex: 0, onClick: () => openCard(w.ref), onKeyDown: onActivate(() => openCard(w.ref)), title: `Open ${w.ref}` } : {})}
                >
                  <div>
                    <div className="wc-t">{w.title ?? w.label}</div>
                    <div className="wc-n">{w.label}</div>
                  </div>
                  {/* Only show the age when it is known — never "· — days". */}
                  <div className="wc-s">{TYPE_LABEL[w.type] ?? 'Waiting'}{w.days != null ? ` · ${w.days} ${w.days === 1 ? 'day' : 'days'}` : ''}</div>
                </div>
              ))}
              {row.waiting.length > WAIT_CAP && (
                <div className="wc-more">+{row.waiting.length - WAIT_CAP} more</div>
              )}
              {canDrillBlockers && (
                <button className="btn" onClick={() => onOpenBlockers(row.id)}>See the blocked cards →</button>
              )}
            </>
          )}
        </div>

        {/* Progress — the cards BEHIND the roll-up's "N at risk · N behind" count,
            answering WHICH cards won't finish and WHY. Worst first (overdue / won't-
            make-it before behind). Each names the % done against the % the calendar
            expects today (the gap the ProgressMini's tick draws, said in words too),
            the health read, and the due date with a plain lateness phrase. Only when
            something is slipping — all-on-track shows no section, like Milestones. */}
        {row.progressCards.length > 0 && (
          <div className="pf-card">
            <h3>Progress — {progHead(row.progressCounts)}</h3>
            {row.progressCards.slice(0, PROG_CAP).map((c) => (
              <div
                className="wc pf-prog is-drillable"
                key={c.ref}
                role="button"
                tabIndex={0}
                onClick={() => openCard(c.ref)}
                onKeyDown={onActivate(() => openCard(c.ref))}
                title={`Open ${c.ref}`}
              >
                <div className="pf-prog-main">
                  <div className="wc-t">{c.title}</div>
                  <div className="wc-n">{c.ref} · {dueLatePhrase(c.dueDate, now)}</div>
                  <div className="pf-prog-bar">
                    <ProgressMini health={miniHealth(c)} showPct={false} />
                    <span className="pf-prog-exp">
                      {c.pct}% done{c.expectedPct != null ? <> · expected ~{c.expectedPct}%</> : null}
                    </span>
                  </div>
                </div>
                <div className="wc-s">{c.label}</div>
              </div>
            ))}
            {row.progressCards.length > PROG_CAP && (
              <div className="wc-more">+{row.progressCards.length - PROG_CAP} more</div>
            )}
          </div>
        )}

        {/* Risks — a steering essential. Surfaces the project's captured risks so
            the field is not dead weight; prompts capture when empty rather than
            hiding the question. The charter's Risk register is read first (where
            risks are actually authored); the free-text field is the fallback for a
            project that only set that. */}
        <div className="pf-card">
          <h3>Risks</h3>
          {row.charterRisks?.length ? (
            <ul className="pf-risks">
              {row.charterRisks.slice(0, RISK_CAP).map((k, i) => (
                <li key={i} className="pf-risk-row">
                  <div className="pf-risk-head">
                    <span className="pf-risk-what">{k.risk || '—'}</span>
                    {k.severity && (
                      <span className={`pf-risk-sev band-${severityBandKey(k.severity)}`}>{k.severity}</span>
                    )}
                  </div>
                  {(k.mitigation || k.owner) && (
                    <div className="pf-risk-mitig">
                      {k.mitigation && <span>Mitigation: {k.mitigation}</span>}
                      {k.owner && <span className="pf-risk-owner">Owner: {k.owner}</span>}
                    </div>
                  )}
                </li>
              ))}
              {row.charterRisks.length > RISK_CAP && (
                <li className="wc-more">+{row.charterRisks.length - RISK_CAP} more on the charter</li>
              )}
            </ul>
          ) : row.risks ? (
            <p className="pf-risk">{row.risks}</p>
          ) : (
            <div className="kv"><span className="k">No risks on the charter yet</span></div>
          )}
        </div>

        {/* Success Measure — how the project will be judged a success. Its OWN card,
            not a line under Risks: a risk (what could go wrong) and a success
            measure (what "done well" looks like) are different steering questions,
            and bundling them under "Risks" left a reader unsure what they were
            reading. Same capture prompt when empty. */}
        <div className="pf-card">
          <h3>Success Measure</h3>
          {richHasText(row.successMeasure) ? (
            <p className="pf-risk">{richText(row.successMeasure)}</p>
          ) : (
            <div className="kv"><span className="k">No success measure captured yet</span></div>
          )}
        </div>

        <div className="pf-card">
          <h3>Phases</h3>
          {row.phases.map((p) => {
            const overdue = p.status === 'active' && (p.goLive === 'past-open' || p.goLive === 'planned-breach');
            const dot: React.CSSProperties =
              p.status === 'done'
                ? { background: 'var(--s-done)' }
                : p.status === 'active'
                  ? { background: overdue ? 'var(--p-critical)' : 'var(--s-inprogress)' }
                  : { background: 'var(--sunk)', border: '1px solid var(--line)' };
            const word = p.status === 'done' ? 'done' : overdue ? 'overdue · open' : p.status === 'active' ? 'active' : 'not started';
            return (
              <div className="phase" key={p.id}>
                <span className="pd" style={dot}></span>
                <span className="pn">{p.name}</span>
                <span className="ps">{word}</span>
              </div>
            );
          })}
        </div>

        <div className="pf-card">
          <h3>Movement — last 7 days</h3>
          {movementChips(row.movement).length === 0 && !row.throughput ? (
            <ul className="mv"><li>No movement in the last 7 days.</li></ul>
          ) : (
            <>
              {movementChips(row.movement).length > 0 && (
                <div className="mv-card-verbs">
                  {movementChips(row.movement).map((c) => (
                    <span className="mv-vchip" key={c.label}><b>{c.n}</b> {c.label}</span>
                  ))}
                </div>
              )}
              {row.throughput && (
                <div className="mv-through-lg">
                  <Spark values={row.throughput.weekly} w={150} h={40} />
                  <span className={`mv-read${row.throughput.dir === 'up' ? ' up' : row.throughput.dir === 'down' ? ' down' : ''}`}>
                    <b>{row.throughput.thisWeek} closed this week</b>{throughputReadingLong(row.throughput).replace(/^\d+ closed this week/, '')}
                  </span>
                </div>
              )}
              {row.aging.length > 0 && (
                <div className="mv-block">
                  <div className="mv-block-h">Work in Flight — Pressing vs Stalled</div>
                  <div className="mv-chips">
                    {row.aging.slice(0, 6).map((a) => (
                      <button
                        type="button"
                        className={`age-chip is-drillable${a.aged ? ' old' : ''}`}
                        key={a.ref}
                        onClick={() => openCard(a.ref)}
                        title={`Open ${a.ref}`}
                      >
                        <span className="ref">{a.ref}</span>{a.aged ? `stalled ${a.days} days` : `${a.days} days, pressing`}
                      </button>
                    ))}
                  </div>
                  <div className="mv-note">
                    &ldquo;Stalled&rdquo; = past this board&rsquo;s own norm of {row.agingNorm} days in flight (its 75th percentile) — flagged above. The muted ones are &ldquo;pressing&rdquo; — moving at a normal pace.
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        </div>

      </div>
    </div>
  );
}

/** The non-zero movement counts as {n, label} chips, in flow order — the report
 *  shows each verb as its own figure rather than the roll-up's one-line join. */
function movementChips(m: MovementCounts): { n: number; label: string }[] {
  const out: { n: number; label: string }[] = [];
  if (m.started) out.push({ n: m.started, label: 'started' });
  if (m.staged) out.push({ n: m.staged, label: 'to staging' });
  if (m.toReview) out.push({ n: m.toReview, label: 'to review' });
  if (m.closed) out.push({ n: m.closed, label: 'closed' });
  if (m.pushedBack) out.push({ n: m.pushedBack, label: 'pushed back' });
  if (m.newBlockers) out.push({ n: m.newBlockers, label: m.newBlockers === 1 ? 'new blocker' : 'new blockers' });
  return out;
}
